/**
 * Restore, and the preview that must come before it (FR-BAK-05, FR-BAK-09).
 *
 * Pure. Restoring is the most destructive thing the app can do — it writes over live
 * records with older ones — so the whole of this file exists to make the user look at
 * what will happen first. **"A restore preview lists what will change before it runs"** is
 * M9's exit criterion, and a preview computed by the same code that performs the write is
 * the only kind worth trusting.
 *
 * Three properties the diff deliberately has:
 *
 *  · **It never reports a deletion.** A snapshot is a point in time, not a specification
 *    of the present. A record created after the backup was taken is not "extra"; deleting
 *    it would lose data the backup was supposed to protect. Those are counted and shown
 *    as untouched.
 *  · **It compares field by field**, so "would overwrite 412 transactions" becomes
 *    "3 differ, 409 are identical". Restoring a week-old backup usually changes almost
 *    nothing, and a preview that cannot say so is one nobody reads.
 *  · **Rollups are excluded.** They are a cache derived from transactions (AD-02) and are
 *    rebuilt after a restore. Restoring stale rollups over correct ones would put the
 *    dashboard wrong in a way that survives until someone notices.
 */

import { diffFields, type AuditChange } from './audit';

export interface Snapshot {
  takenAt: string;
  appVersion?: string;
  collections: Record<string, Record<string, unknown>[]>;
  counts?: Record<string, number>;
}

export type ChangeKind = 'added' | 'changed' | 'identical';

export interface RecordChange {
  collection: string;
  id: string;
  kind: ChangeKind;
  /** Field-level, for a `changed` record. Empty otherwise. */
  changes: AuditChange[];
  /** A human label, so the preview reads as records rather than as ids. */
  label: string;
}

export interface CollectionPreview {
  collection: string;
  added: number;
  changed: number;
  identical: number;
  /** Live records the snapshot does not contain. Never deleted — see the header. */
  untouched: number;
  samples: RecordChange[];
}

export interface RestorePreview {
  takenAt: string;
  collections: CollectionPreview[];
  totalWrites: number;
  totalUntouched: number;
  problems: string[];
}

export class RestoreError extends Error {}

/**
 * Collections a restore may write.
 *
 * `rollups` is absent by design (see the header). `users` is absent for a different
 * reason: restoring a capability document from a backup could re-grant access that was
 * deliberately revoked, which is a security regression disguised as a recovery.
 */
export const RESTORABLE = [
  'accounts', 'categories', 'tags', 'transactions', 'settings',
  'loans', 'receivables', 'recurring', 'recurringHandled', 'tasks', 'budgets', 'imports',
] as const;

/** Fields that always differ and never matter. */
const NOISE = new Set(['updatedAt', 'createdAt', 'at', 'importedAt', 'searchTokens']);

export function isRestorable(collection: string): boolean {
  return (RESTORABLE as readonly string[]).includes(collection);
}

/**
 * Is this file plausibly one of our snapshots?
 *
 * Checked before anything else, because the failure mode of restoring an unrecognised
 * JSON file is writing garbage over a working database.
 */
export function validateSnapshot(value: unknown): { snapshot: Snapshot | null; problem: string | null } {
  if (!value || typeof value !== 'object') {
    return { snapshot: null, problem: 'That file is not JSON we recognise.' };
  }

  const candidate = value as Partial<Snapshot>;
  if (typeof candidate.takenAt !== 'string' || !candidate.collections) {
    return { snapshot: null, problem: 'That file has no `takenAt` or `collections` — it is not a backup.' };
  }
  if (typeof candidate.collections !== 'object' || Array.isArray(candidate.collections)) {
    return { snapshot: null, problem: '`collections` is the wrong shape.' };
  }

  const known = Object.keys(candidate.collections).filter(isRestorable);
  if (known.length === 0) {
    return { snapshot: null, problem: 'That backup contains none of this app’s collections.' };
  }

  return { snapshot: candidate as Snapshot, problem: null };
}

function labelFor(collection: string, record: Record<string, unknown>, id: string): string {
  const named = record.name ?? record.description ?? record.person ?? record.text ?? record.title;
  if (typeof named === 'string' && named.trim()) return named.trim();
  if (collection === 'transactions' && record.dateKey) {
    return `${String(record.dateKey)} · ${String(record.amountPaise ?? '')}`;
  }
  return id;
}

function meaningfulDiff(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): AuditChange[] {
  return diffFields(before, after).filter((change) => !NOISE.has(change.field));
}

/**
 * What restoring this snapshot would do.
 *
 * `live` is the current contents, keyed by collection then id. Both sides are plain
 * objects, so this runs identically in the browser preview and in a test.
 */
export function previewRestore(
  snapshot: Snapshot,
  live: Record<string, Record<string, Record<string, unknown>>>,
  { samplesPerCollection = 5 } = {},
): RestorePreview {
  const collections: CollectionPreview[] = [];
  const problems: string[] = [];
  let totalWrites = 0;
  let totalUntouched = 0;

  for (const name of Object.keys(snapshot.collections)) {
    if (!isRestorable(name)) {
      // Said out loud rather than dropped silently: a user who backed up their rollups
      // should know why they are not coming back.
      problems.push(
        name === 'rollups'
          ? 'Rollups are not restored — they rebuild from the transactions afterwards.'
          : name === 'users' || name === 'auditLog'
            ? `${name} is not restored: bringing it back could undo a deliberate change.`
            : `${name} is not a collection this app restores.`,
      );
      continue;
    }

    const records = snapshot.collections[name] ?? [];
    const current = live[name] ?? {};
    const samples: RecordChange[] = [];
    let added = 0;
    let changed = 0;
    let identical = 0;

    for (const record of records) {
      const id = String(record.id ?? '');
      if (!id) {
        problems.push(`A record in ${name} has no id and cannot be restored.`);
        continue;
      }

      const { id: _drop, ...fields } = record;
      const existing = current[id];

      if (!existing) {
        added += 1;
        if (samples.length < samplesPerCollection) {
          samples.push({ collection: name, id, kind: 'added', changes: [], label: labelFor(name, fields, id) });
        }
        continue;
      }

      const differences = meaningfulDiff(existing, fields);
      if (differences.length === 0) {
        identical += 1;
        continue;
      }

      changed += 1;
      if (samples.length < samplesPerCollection) {
        samples.push({
          collection: name, id, kind: 'changed', changes: differences, label: labelFor(name, fields, id),
        });
      }
    }

    const inSnapshot = new Set(records.map((r) => String(r.id ?? '')));
    const untouched = Object.keys(current).filter((id) => !inSnapshot.has(id)).length;

    totalWrites += added + changed;
    totalUntouched += untouched;

    collections.push({ collection: name, added, changed, identical, untouched, samples });
  }

  return {
    takenAt: snapshot.takenAt,
    collections: collections.sort((a, b) => b.added + b.changed - (a.added + a.changed)),
    totalWrites,
    totalUntouched,
    problems,
  };
}

/** The records a restore would actually write. Derived from the same pass as the preview. */
export function restoreWrites(
  snapshot: Snapshot,
  live: Record<string, Record<string, Record<string, unknown>>>,
): { collection: string; id: string; data: Record<string, unknown> }[] {
  const writes: { collection: string; id: string; data: Record<string, unknown> }[] = [];

  for (const name of Object.keys(snapshot.collections)) {
    if (!isRestorable(name)) continue;
    const current = live[name] ?? {};

    for (const record of snapshot.collections[name] ?? []) {
      const id = String(record.id ?? '');
      if (!id) continue;

      const { id: _drop, ...fields } = record;
      const existing = current[id];
      if (existing && meaningfulDiff(existing, fields).length === 0) continue;

      writes.push({ collection: name, id, data: fields });
    }
  }

  return writes;
}

/**
 * A sentence for the confirmation (FR-BAK-09).
 *
 * States the untouched count as well as the writes, because "1,204 records will be left
 * alone" is the reassurance that makes the number that *will* change readable.
 */
export function describeRestore(preview: RestorePreview): string {
  if (preview.totalWrites === 0) {
    return `Nothing to restore — the ${preview.totalUntouched} live records already match this backup.`;
  }

  const added = preview.collections.reduce((n, c) => n + c.added, 0);
  const changed = preview.collections.reduce((n, c) => n + c.changed, 0);

  const parts = [];
  if (added > 0) parts.push(`${added} record${added === 1 ? '' : 's'} restored`);
  if (changed > 0) parts.push(`${changed} overwritten with the backup's version`);

  return `${parts.join(' and ')}. ${preview.totalUntouched} newer record${
    preview.totalUntouched === 1 ? '' : 's'
  } will be left alone — a restore never deletes.`;
}
